import { generateRootObjectSchema, generateArraySchema, generateObjectSchema, generateStringSchema, generateUrlSchema, generateColorSchema, generateBooleanSchema } from "components/cards/common/schema";
import { BaseAtomSpec, generateAtomContainerSchema } from "components/cards/atoms/BaseAtomSpec";
import { AudioParticleSpec, CaptionedParticleSpec, ImageParticleSpec, RichTextParticleSpec, TextParticleSpec, VideoParticleSpec } from "components/cards/particles/YinzCam";

export const specification = new BaseAtomSpec("Accordion Card", generateRootObjectSchema({
    container: generateAtomContainerSchema({ defaultValues: { themeMode: 'primary' } }),
    accordionGap: generateStringSchema("Accordion Tab Gap", "Specify a gap between accordion tabs"),
    contentSideBySide: generateBooleanSchema("Content Side By Side", "Place content to the right of the heading instead of below it.", {
        defaultValue: false
    }),
    allowMultipleOpen: generateBooleanSchema("Allow Multiple Open Tabs", "Allow multiple accordion tabs to stay open at the same time.", {
        defaultValue: false
    }),
    imgOpenIcon: ImageParticleSpec.generateSchema("Open Icon", "Select an icon for toggling accordion", {
        defaultValues: { container: { width: "2rem", height: "2rem" }, icon: 'yc_icon_add' }
    }),
    imgCloseIcon: ImageParticleSpec.generateSchema("Close Icon", "Select an icon for toggling accordion", {
        defaultValues: { container: { width: "2rem", height: "2rem" }, icon: 'yc_icon_subtract' }
    }),
    toggleIconMode: generateStringSchema("Toggle Icon Mode", "Use images from CMS, CSS animated +/- icon, or CSS animated arrow icon.", {
        choices: ["image", "css-plus-minus", "css-arrow"],
        choiceTitles: ["Image", "CSS +/-", "CSS Arrow"],
        defaultValue: "image"
    }),
    useCssIcons: generateBooleanSchema("Use CSS Icons", "Render the open/close icon with CSS for animation.", {
        defaultValue: false
    }),
    cssIconColor: generateColorSchema("CSS Icon Color", "Color for CSS toggle icons."),
    cssIconSize: generateStringSchema("CSS Icon Container Size", "CSS Icon Container Size"),
    cssIconBarLength: generateStringSchema("CSS Icon Bar Length", "Length of the plus/minus bars."),
    cssIconBarThickness: generateStringSchema("CSS Icon Bar Thickness", "Thickness of the plus/minus bars."),
    cssArrowThickness: generateStringSchema("CSS Arrow Thickness", "Thickness of the CSS arrow icon."),
    cssIconTransitionDuration: generateStringSchema("CSS Icon Transition Duration", "Duration for the plus/minus animation."),
    iconPaddingTop: generateStringSchema("Icon Padding Top", "Top padding for the open/close icon."),
    iconPaddingRight: generateStringSchema("Icon Padding Right", "Right padding for the open/close icon."),
    iconPaddingBottom: generateStringSchema("Icon Padding Bottom", "Bottom padding for the open/close icon."),
    iconPaddingLeft: generateStringSchema("Icon Padding Left", "Left padding for the open/close icon."),
    iconAlignment: generateStringSchema("Icon Alignment", "Vertical alignment for the icon container (e.g. start, center, end)."),
    useFullBleedDivider: generateBooleanSchema("Use Full Bleed Divider", "Use Full Bleed Divider.", {
        defaultValue: false
    }),
    textDefaults: TextParticleSpec.generateSchema("Text Defaults", "Default styling for text sections of articles.", {
        defaultValues: { container: { centerContentHorizontally: false } }
    }),
    imageDefaults: ImageParticleSpec.generateSchema("Image Defaults", "Default styling for image blocks of articles.", {
        defaultValues: { container: { fillWidth: true, centerContentHorizontally: true, fillHeight: false } }
    }),
    videoDefaults: VideoParticleSpec.generateSchema("Video Defaults", "Default styling for video blocks of articles.", {
        defaultValues: { container: { fillWidth: true, centerContentHorizontally: true, fillHeight: false }, controls: true }
    }),
    audioDefaults: AudioParticleSpec.generateSchema("Audio Defaults", "Default styling for audio blocks of articles."),
    captionDefaults: CaptionedParticleSpec.generateSchema("Caption Defaults", "Default styling for captions on blocks within articles."),
    itemBackgroundColor: generateColorSchema("Item Background Color", ""),
    activeItemBackgroundColor: generateColorSchema("Active Item Background Color", "Background color for open accordion items."),
    activeItemBackgroundAnimate: generateBooleanSchema("Active Background Animate", "Animate the background color change when opening/closing.", {
        defaultValue: false
    }),
    activeItemBackgroundTransitionDuration: generateStringSchema("Active Background Transition Duration", "Duration for active background transition animation."),
    itemPadding: generateStringSchema("Item Padding", ""),
    itemFixedHeight: generateStringSchema("Accordion Tab Fixed Height", "Fixed height for each accordion tab."),
    itemBorderRadius: generateStringSchema("Item Border Radius", ""),
    itemDividerColor: generateColorSchema("Item Divider Color", ""),
    itemDividerThickness: generateStringSchema("Divider Thickness", "Thickness of the divider line (e.g. 1px)."),
    itemDividerHidden: generateBooleanSchema("Item Divider Hidden", ""),
    showTopDivider: generateBooleanSchema("Show Top Divider", "Show a divider above the first accordion tab.", {
        defaultValue: false
    }),
    hideLastDivider: generateBooleanSchema("Hide Last Divider", "Hide the divider under the last accordion tab.", {
        defaultValue: false
    }),
    activeItemDividerColor: generateColorSchema("Active Divider Color", "Divider color for the top edge of an open tab."),
    accordionTabs: generateArraySchema("Accordion Tabs", "Accordion tabs to display.",
        generateObjectSchema("Accordion", "Tab with title and content.", {
            heading: TextParticleSpec.generateSchema("Title", "Add a title", {
                defaultValues: { size: "1.5rem", weight: "700" }
            }),
            useRichTextTitle: generateBooleanSchema("Use Rich Text Title", "Use the rich rext title instead of a text title.", {
                defaultValue: false
             }),
            richTextTitle: RichTextParticleSpec.generateSchema("Rich Text Title", "Add rich text title"),
            content: RichTextParticleSpec.generateSchema("Content", "Add content", {
                defaultValues: {
                    size: "1rem",
                    weight: "400",
                    container: {
                        margin: "0",
                        padding: "1rem"
                    }
                }
            }),
            beforeContentActive: RichTextParticleSpec.generateSchema("Content (Inactive)", "Add content to show when the accordion tab is closed."),
            articleBody: generateArraySchema("Article Body", "The article body as a series of component props.",
                generateObjectSchema("Body Item", "An item that is placed in the body of the article.", {}, { additionalProperties: true })
            ),
            linkUrl: generateUrlSchema("URL", "Specify the external URL to open")
        })
    ),
}));
